import Big from 'big.js';
import { LineTab, LineTabs, LineTabsList } from 'components/LineTabs';
import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput';
import PercentageInput from 'components/forms/inputs/rhf-inputs/PercentageInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { ToggleGroup, ToggleGroupItem } from 'components/ui/toggle-group';
import { formatToHours } from 'components/util/HoursDisplay';
import { formatAmount } from 'components/util/MoneyDisplay';
import PaymentCurrencyNotice from 'pages/invoices/layout/views/components/PaymentCurrencyNotice';
import { FC, useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useUpdateInvoiceTimecardsMutation } from 'store/api/invoice-details';
import {
    InvoiceTimecardBulkChangeForm,
    InvoiceTimecardBulkUpdateDto,
    InvoiceTimecardForm,
} from 'types/invoice-details';
import { t } from 'utils/getTranslations';
import { secondsToHours } from 'utils/time';
import { paymentCurrencyInputClassName } from 'utils/usePaymentCurrencyMode';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import { ModalFooter } from '../ModalFooter';
import { ModalHeader } from '../ModalHeader';

interface Props {
    onClose: () => void;
    initialAmountBefore: number;
    initialBilledTimeInSeconds: number;
    timecards: InvoiceTimecardForm[];
    invoiceId: number;
    matterId: number;
    isPaymentCurrencyMode?: boolean;
    currency?: string;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
    /** The new design switches panes with pressed buttons, not tabs: the pane is a plain region. */
    isNew: boolean;
}

const defaultValues: InvoiceTimecardBulkChangeForm = {
    amountBefore: 0,
    amountAfter: '',
    billedTimeInHours: '0h',
    adjustPct: '',
};

const TimecardBulkCapFormModal: FC<Props> = ({
    onClose,
    initialAmountBefore,
    initialBilledTimeInSeconds,
    timecards,
    invoiceId,
    matterId,
    isPaymentCurrencyMode,
    currency,
}) => {
    const [tabValue, setTabValue] = useState(0);
    const { displayDecimalScale } = useTimecardPrecision();
    const isNew = useIsNewDesign();

    // Only shown: the adjustment is worked out from `initialAmountBefore`, so the new design may write it as an amount.
    const amountBefore = isNew ? formatAmount(initialAmountBefore) : new Big(initialAmountBefore).toFixed(2);
    const billedTimeInHours = formatToHours(
        secondsToHours(initialBilledTimeInSeconds, displayDecimalScale),
        displayDecimalScale
    );

    const methods = useForm<InvoiceTimecardBulkChangeForm>({
        defaultValues: { ...defaultValues, amountBefore, billedTimeInHours },
        values: { ...defaultValues, amountBefore, billedTimeInHours },
    });

    const { handleSubmit, control } = methods;

    const [updateInvoiceTimecards] = useUpdateInvoiceTimecardsMutation();

    const onAmountAdjustment = useCallback(async () => {
        await handleSubmit(async ({ amountAfter }) => {
            const totalManualAdjustment = initialAmountBefore - Number(amountAfter);
            const dtoList: InvoiceTimecardBulkUpdateDto[] = [];

            for (const tc of timecards) {
                const timecardProportion = Number(tc.totalBeforeAdjustment) / initialAmountBefore;
                const timecardNewManualAdjustment = -new Big(totalManualAdjustment * timecardProportion).toFixed(2);

                dtoList.push({
                    id: tc.id,
                    billedTimeInSeconds: tc.billedTimeInSeconds,
                    hourlyPrice: null,
                    manualAdjustmentAmount: timecardNewManualAdjustment,
                    newMatterId: tc.matter?.id || null,
                    editIntentTypeCode: tc.editIntentTypeCode,
                    isNonBillable: tc.isNonBillable,
                    preserveNonBillableHours: tc.preserveNonBillableHours,
                    newMatterAssignmentId: tc.matterAssignment?.id || null,
                });
            }
            await updateInvoiceTimecards({ invoiceId, matterId, dtoList }).unwrap().catch(console.error);
            onClose();
        })();
    }, [handleSubmit, initialAmountBefore, updateInvoiceTimecards, invoiceId, matterId, onClose, timecards]);

    const onBilledTimeAdjustment = useCallback(async () => {
        await handleSubmit(async ({ adjustPct }) => {
            if (!isNaN(Number(adjustPct)) && adjustPct !== 0) {
                const dtoList: InvoiceTimecardBulkUpdateDto[] = timecards.map((tc) => ({
                    id: tc.id,
                    billedTimeInSeconds: (tc.timecard?.billedTimeInSeconds ?? 0) * (1 + Number(adjustPct) / 100),
                    hourlyPrice: null,
                    manualAdjustmentAmount: tc.manualAdjustmentAmount,
                    newMatterId: tc.matter?.id || null,
                    editIntentTypeCode: tc.editIntentTypeCode,
                    isNonBillable: tc.isNonBillable,
                    preserveNonBillableHours: tc.preserveNonBillableHours,
                    newMatterAssignmentId: tc.matterAssignment?.id || null,
                }));
                await updateInvoiceTimecards({ invoiceId, matterId, dtoList }).unwrap().catch(console.error);
            }
            onClose();
        })();
    }, [handleSubmit, timecards, updateInvoiceTimecards, invoiceId, matterId, onClose]);

    const paneClassName = 'new:px-6 new:pb-6 h-full overflow-y-auto px-4 pt-5 pb-1.5 max-sm:pt-6';
    const amountClassName = isPaymentCurrencyMode ? paymentCurrencyInputClassName : undefined;
    const amountSuffix = isNew ? currency : undefined;

    return (
        <>
            <ModalHeader
                kit
                title={t('invoices.change-amount')}
                description={
                    <FormattedMessage id="invoices.bulk.for-selected-timecards" values={{ count: timecards.length }} />
                }
                onCancel={onClose}
            />
            {isPaymentCurrencyMode && currency && <PaymentCurrencyNotice currency={currency} className="mx-4 mt-2" />}
            <div className="w-full">
                {isNew ? (
                    // The new design's two-segment control: each answer a pressed button over the one pane it shows.
                    <ToggleGroup
                        value={[String(tabValue)]}
                        onValueChange={(value: string[]) => {
                            if (value[0]) setTabValue(Number(value[0]));
                        }}
                        variant="outline"
                        spacing={0}
                        className="mx-6 mt-5 grid w-auto grid-cols-2"
                    >
                        <ToggleGroupItem value="0" aria-controls="simple-tabpanel-0">
                            {t('invoices.adjust-cost')}
                        </ToggleGroupItem>
                        <ToggleGroupItem value="1" aria-controls="simple-tabpanel-1">
                            {t('invoices.adjust-pct')}
                        </ToggleGroupItem>
                    </ToggleGroup>
                ) : (
                    <LineTabs
                        value={tabValue}
                        onValueChange={(value) => setTabValue(Number(value))}
                        className="px-4 pt-2"
                    >
                        <LineTabsList>
                            <LineTab value={0} size="compact" {...a11yProps(0)}>
                                {t('invoices.adjust-cost')}
                            </LineTab>
                            <LineTab value={1} size="compact" {...a11yProps(1)}>
                                {t('invoices.adjust-pct')}
                            </LineTab>
                        </LineTabsList>
                    </LineTabs>
                )}
                <CustomTabPanel value={tabValue} index={0} isNew={isNew}>
                    <div className={paneClassName}>
                        <div className="flex flex-col gap-4">
                            <p className="text-body1">
                                <FormattedMessage id="invoices.cap-selected-timecards-at" />
                            </p>
                            <FormProvider {...methods}>
                                <div className="flex flex-row gap-4">
                                    <DecimalInput
                                        control={control}
                                        name="amountBefore"
                                        label="invoices.original-amount"
                                        required={!isNew}
                                        disabled
                                        suffixComponent={amountSuffix}
                                        wrapperClassName={amountClassName}
                                    />
                                    <DecimalInput
                                        control={control}
                                        name="amountAfter"
                                        label="invoices.target-amount"
                                        required
                                        autoFocus
                                        suffixComponent={amountSuffix}
                                        wrapperClassName={amountClassName}
                                    />
                                </div>
                            </FormProvider>
                        </div>
                    </div>
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={1} isNew={isNew}>
                    <div className={paneClassName}>
                        <div className="flex flex-col gap-4">
                            <p className="text-body1">
                                <FormattedMessage id="invoices.select-pct-to-adjust" />
                            </p>
                            <FormProvider {...methods}>
                                <div className="flex flex-row gap-4">
                                    <TextInput
                                        control={control}
                                        name="billedTimeInHours"
                                        label="invoices.total-billed-time"
                                        required={!isNew}
                                        disabled
                                    />
                                    <PercentageInput
                                        control={control}
                                        name="adjustPct"
                                        label="invoices.adjust-pct"
                                        required
                                        autoFocus
                                        allowNegative
                                    />
                                </div>
                            </FormProvider>
                        </div>
                    </div>
                </CustomTabPanel>
            </div>
            <ModalFooter
                kit
                onCancel={onClose}
                onSave={() => {
                    void (tabValue === 0 ? onAmountAdjustment() : onBilledTimeAdjustment());
                }}
                saveButtonTextId="adjust"
                height={65}
            />
        </>
    );
};

export default TimecardBulkCapFormModal;

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, isNew, ...other } = props;

    return (
        <div
            role={isNew ? undefined : 'tabpanel'}
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={isNew ? undefined : `simple-tab-${index}`}
            {...other}
        >
            {value === index && <div>{children}</div>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}
